import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, ChevronDown, Check, RotateCcw, ArrowLeft } from "lucide-react";
import { Project, WorkflowPipeline, WorkflowStatus } from "@/lib/api";

export const statusColorClasses: Record<string, string> = {
  gray:   "bg-slate-200 text-slate-900 border border-slate-400 hover:bg-slate-300",
  blue:   "bg-blue-200 text-blue-900 border border-blue-400 hover:bg-blue-300",
  purple: "bg-purple-200 text-purple-900 border border-purple-400 hover:bg-purple-300",
  orange: "bg-orange-200 text-orange-900 border border-orange-400 hover:bg-orange-300",
  red:    "bg-red-200 text-red-900 border border-red-500 hover:bg-red-300",
  green:  "bg-emerald-200 text-emerald-900 border border-emerald-500 hover:bg-emerald-300",
  amber:  "bg-amber-200 text-amber-900 border border-amber-400 hover:bg-amber-300",
  teal:   "bg-teal-200 text-teal-900 border border-teal-400 hover:bg-teal-300",
  indigo: "bg-indigo-200 text-indigo-900 border border-indigo-400 hover:bg-indigo-300",
};

const statusDotColors: Record<string, string> = {
  gray:   "#94a3b8",
  blue:   "#3b82f6",
  purple: "#a855f7",
  orange: "#f97316",
  red:    "#ef4444",
  green:  "#10b981",
  amber:  "#f59e0b",
  teal:   "#14b8a6",
  indigo: "#6366f1",
};

export function findStatus(statusId: string | null | undefined, pipelines: WorkflowPipeline[]): WorkflowStatus | null {
  if (!statusId) return null;
  for (const p of pipelines) {
    const s = p.stages.find((s) => s.id === statusId);
    if (s) return s;
  }
  return null;
}

export function findPipelineForStatus(statusId: string | null | undefined, pipelines: WorkflowPipeline[]): WorkflowPipeline | null {
  if (!statusId) return null;
  return pipelines.find((p) => p.stages.some((s) => s.id === statusId)) ?? null;
}

interface WorkflowStatusBadgeProps {
  project: Project;
  pipelines: WorkflowPipeline[];
  onStatusChange: (projectId: string, statusId: string) => void;
  isPending?: boolean;
}

export function WorkflowStatusBadge({ project, pipelines, onStatusChange, isPending }: WorkflowStatusBadgeProps) {
  const [open, setOpen] = useState(false);
  const currentStage = findStatus(project.workflow_status_id, pipelines);
  const currentPipeline = findPipelineForStatus(project.workflow_status_id, pipelines);
  const preInterruptStage = findStatus(project.pre_interrupt_status_id, pipelines);

  const prevNormalStage = useMemo(() => {
    if (!currentStage || !currentPipeline || currentStage.status_type !== "normal") return null;
    const normals = currentPipeline.stages
      .filter(s => s.status_type === "normal")
      .sort((a, b) => a.sequence_order - b.sequence_order);
    const idx = normals.findIndex(s => s.id === currentStage.id);
    return idx > 0 ? normals[idx - 1] : null;
  }, [currentStage, currentPipeline]);

  const displayPipelines = currentPipeline ? [currentPipeline] : pipelines;

  const handleSelect = (statusId: string) => {
    setOpen(false);
    onStatusChange(project.id, statusId);
  };

  const colorKey = currentStage?.color ?? "gray";
  const badgeClass = statusColorClasses[colorKey] ?? statusColorClasses.gray;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          disabled={isPending}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium transition-colors shrink-0 max-w-[160px] truncate ${
            currentStage ? badgeClass : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
          title={currentStage ? `Pipeline: ${currentPipeline?.name ?? ""}\nStatus: ${currentStage.name}` : "Set workflow status"}
        >
          {isPending ? (
            <Loader2 className="w-3 h-3 animate-spin shrink-0" />
          ) : (
            <span className="truncate">{currentStage?.name ?? "Set status"}</span>
          )}
          <ChevronDown className="w-3 h-3 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <div className="max-h-96 overflow-y-auto">
          {currentStage?.status_type === "interrupt" && preInterruptStage && (
            <div className="p-2 border-b">
              <button
                onClick={() => handleSelect(preInterruptStage.id)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
              >
                <RotateCcw className="w-3 h-3 shrink-0" />
                Resume: {preInterruptStage.name}
              </button>
            </div>
          )}
          {prevNormalStage && (
            <div className="p-2 border-b">
              <button
                onClick={() => handleSelect(prevNormalStage.id)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs font-medium bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
              >
                <ArrowLeft className="w-3 h-3 shrink-0" />
                Send back: {prevNormalStage.name}
              </button>
            </div>
          )}
          {displayPipelines.map((pipeline) => {
            const normalStages = pipeline.stages.filter((s) => s.status_type === "normal").sort((a, b) => a.sequence_order - b.sequence_order);
            const interruptStages = pipeline.stages.filter((s) => s.status_type === "interrupt");
            const terminalStages = pipeline.stages.filter((s) => s.status_type === "terminal");
            return (
              <div key={pipeline.id}>
                {displayPipelines.length > 1 && (
                  <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted/50">
                    {pipeline.name}
                  </div>
                )}
                {normalStages.map((stage, idx) => (
                  <button key={stage.id} onClick={() => handleSelect(stage.id)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted transition-colors text-left ${stage.id === project.workflow_status_id ? "bg-muted/60 font-semibold" : ""}`}
                  >
                    <span className="text-muted-foreground w-4 text-right shrink-0">{idx + 1}</span>
                    <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: statusDotColors[stage.color] ?? "#94a3b8" }} />
                    <span className="truncate">{stage.name}</span>
                    {stage.id === project.workflow_status_id && <Check className="w-3 h-3 ml-auto shrink-0 text-primary" />}
                  </button>
                ))}
                {interruptStages.length > 0 && (
                  <>
                    <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-t">Pause / Interrupt</div>
                    {interruptStages.map((stage) => (
                      <button key={stage.id} onClick={() => handleSelect(stage.id)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted transition-colors text-left ${stage.id === project.workflow_status_id ? "bg-muted/60 font-semibold" : ""}`}
                      >
                        <span className="w-4 shrink-0" />
                        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: statusDotColors[stage.color] ?? "#94a3b8" }} />
                        <span className="truncate">{stage.name}</span>
                        {stage.id === project.workflow_status_id && <Check className="w-3 h-3 ml-auto shrink-0 text-primary" />}
                      </button>
                    ))}
                  </>
                )}
                {terminalStages.length > 0 && (
                  <>
                    <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-t">End States</div>
                    {terminalStages.map((stage) => (
                      <button key={stage.id} onClick={() => handleSelect(stage.id)}
                        className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-muted transition-colors text-left ${stage.id === project.workflow_status_id ? "bg-muted/60 font-semibold" : ""}`}
                      >
                        <span className="w-4 shrink-0" />
                        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: statusDotColors[stage.color] ?? "#94a3b8" }} />
                        <span className="truncate">{stage.name}</span>
                        {stage.id === project.workflow_status_id && <Check className="w-3 h-3 ml-auto shrink-0 text-primary" />}
                      </button>
                    ))}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
